/**
 * The paths a person WITHOUT an account is allowed to be standing on.
 *
 * ─── THE DEFECT THIS EXISTS TO END (2026-08-05, P0) ───────────────────────
 *
 * `_authenticated.tsx` carries a module-scope store subscription that sends a
 * person to /login the moment auth resolves to `unauthenticated`. It lives at
 * module scope in a NON-lazy half of the route tree, so it is installed on
 * EVERY page load — including pages that are not under `_authenticated` at
 * all. Its only exemption was the literal string '/login'.
 *
 * An invited person opens `/signup?token=…`. They have no account, so
 * `GET /api/auth/me` answers 401, the store flips to `unauthenticated`, and
 * the subscription threw them onto a sign-in form they have no password for.
 * The invitation had already loaded successfully — the trace was
 * `NAV /signup → 401 /me → 200 /api/auth/invitation/<token> → NAV /login`.
 * The one case that worked was a person who was ALREADY signed in under the
 * invited address: the door opened only for people who did not need it.
 *
 * ─── WHY A SHARED LIST AND NOT A SECOND STRING COMPARISON ─────────────────
 *
 * The bug was not "somebody forgot /signup". It was that the set of public
 * paths was written as an inline literal at the one site that happened to
 * need it, so every new public page inherits the defect by default and
 * discovers it in production, in front of the person least able to recover.
 * Adding a public route now means adding it HERE, once, and the guard
 * follows. `/login` stays in the list rather than staying inline, so there is
 * exactly one answer to "is this page public".
 *
 * ─── THIS CANNOT OPEN A DOOR ──────────────────────────────────────────────
 *
 * Nothing here grants access to anything. The routes under `_authenticated`
 * are still gated by that layout's `beforeLoad` redirect and by its refusal
 * to render the shell for any status other than `authenticated`; the gateway
 * still answers 401 to every unauthenticated API call. The ONLY behaviour
 * this changes is whether a person standing on a page that was always public
 * gets yanked off it. `/signup` is not, and never was, a route under
 * `_authenticated` — so no protected surface becomes reachable.
 */

/**
 * Exact pathnames served to people who may have no account at all.
 *
 * Keep this in step with the public routes in `src/routes/` and with the
 * gateway's public lane (`apps/chaos-gateway/src/wiring/public-api.ts`).
 */
export const PUBLIC_PATHS: readonly string[] = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
];

/**
 * True when `pathname` is one of the public pages.
 *
 * Exact match, with one tolerance: a single trailing slash. Prefix matching
 * is deliberately NOT used — `/login-as-someone-else` must not inherit
 * `/login`'s exemption just because it starts the same way.
 */
export function isPublicPath(pathname: string | null | undefined): boolean {
  if (typeof pathname !== 'string' || pathname.length === 0) return false;
  const normalised =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return PUBLIC_PATHS.includes(normalised);
}
